// 홈 상단 카드(2026-10-19 · 어드민 「알림 관리 › 카드 발송」) — 순수 함수 · IO 없음.
//   서버: supabase/migrations/20261027100000_home_card.sql (admin_send_home_card · my_home_cards · home_card_ack …)
//   🔒 글자 수 상한·카드 색·링크 규칙은 서버 체크·home_card_link_ok 와 같아야 한다 — 한쪽만 바꾸면 화면은 통과하는데 서버가 거부한다.
//   받는 사람 계산은 공지와 같은 resolve_announcement_audience(대상 JSON 은 아래 audienceFor).

export const CARD_TITLE_MAX = 40;
export const CARD_BODY_MAX = 160;
export const CARD_CTA_MAX = 12;
export const CARD_MAX_DAYS = 90;
export const CARD_TONES = ["brand", "warn", "plain"];
export const CARD_DEFAULT_CTA = "자세히 보기";
/** 홈에 한 번에 펼쳐 보이는 카드 수(A안 · 나머지는 「카드 N개 더 보기」) */
export const CARD_VISIBLE_DEFAULT = 2;

const RE_INTERNAL = /^\/(?:$|[^/\\])/;
const RE_HTTPS = /^https:\/\/[^\s/$.?#][^\s]*$/i;

/**
 * 링크 판정 — 비어 있으면 { kind: null }, 레이블리 안 주소(/…)면 internal, https:// 면 external, 그 밖은 invalid.
 * `//evil.com`(프로토콜 상대 주소)·`javascript:`·`http://` 는 invalid — 서버 home_card_link_ok 와 같은 규칙.
 */
export function normalizeCardLink(link) {
  const v = typeof link === "string" ? link.trim() : "";
  if (!v) return { kind: null, href: null };
  if (RE_INTERNAL.test(v)) return { kind: "internal", href: v };
  if (RE_HTTPS.test(v)) return { kind: "external", href: v };
  return { kind: "invalid", href: null };
}

/** 'YYYY-MM-DD'(한국 날짜) → 그날 한국 시각 24:00 의 ISO 문자열. 형식이 틀리면 null */
export function endOfKstDayIso(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateStr || ""));
  if (!m) return null;
  const y = Number(m[1]); const mo = Number(m[2]); const d = Number(m[3]);
  const t = Date.UTC(y, mo - 1, d + 1, 0, 0, 0) - 9 * 3600 * 1000; // 다음 날 0시 KST = 그날 24:00
  const check = new Date(Date.UTC(y, mo - 1, d));
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null;
  return new Date(t).toISOString();
}

/** 지금 기준 한국 날짜 'YYYY-MM-DD' (+days) */
export function kstDate(days = 0, now = new Date()) {
  const k = new Date(now.getTime() + 9 * 3600 * 1000 + days * 86400000);
  return k.toISOString().slice(0, 10);
}

/** 카드가 끝나는 한국 날짜(「10/27까지」 표기용) — ends_at 은 그날 24:00 이라 1초 앞으로 당겨 날짜를 읽는다 */
export function cardUntilLabel(endsAt) {
  if (!endsAt) return "";
  const t = new Date(endsAt).getTime();
  if (!Number.isFinite(t)) return "";
  const k = new Date(t - 1000 + 9 * 3600 * 1000);
  return `${k.getUTCMonth() + 1}/${k.getUTCDate()}`;
}

/**
 * 받는 사람 고르기 → resolve_announcement_audience 대상 JSON.
 *   users: 고른 이메일 · work: 고른 작품(승인 참여자 — 둘 이상이면 합집합) · group: 저장된 그룹
 *   고른 것이 없으면 null.
 */
export function audienceFor({ mode, emails = [], workIds = [], groupId = null } = {}) {
  if (mode === "users") {
    const list = [...new Set((emails || []).map((e) => String(e || "").trim()).filter(Boolean))];
    return list.length ? { type: "users", emails: list } : null;
  }
  if (mode === "work") {
    const ids = [...new Set((workIds || []).map((w) => String(w || "").trim()).filter(Boolean))];
    if (!ids.length) return null;
    const parts = ids.map((id) => ({ type: "work_participants", licensed_video_id: id }));
    return parts.length === 1 ? parts[0] : { type: "any_of", parts };
  }
  if (mode === "group") return groupId ? { type: "group", group_id: groupId } : null;
  return null;
}

/** 발송 내역·확인 창에 적는 「받는 사람」 한 줄 */
export function audienceLabelFor({ mode, emails = [], works = [], groupName = "" } = {}) {
  if (mode === "users") return `사용자 지정 · ${emails.length}명`;
  if (mode === "work") {
    const titles = works.map((w) => `〈${w.title || "작품"}〉`);
    if (!titles.length) return "작품 승인자";
    return `작품 승인자 · ${titles.slice(0, 2).join(", ")}${titles.length > 2 ? ` 외 ${titles.length - 2}개` : ""}`;
  }
  if (mode === "group") return `저장된 그룹 · ${groupName || "그룹"}`;
  return "";
}

/**
 * 작성 폼 검사 — 보내기 전에 화면에서 먼저 막는다(서버도 같은 규칙으로 다시 막는다).
 * @returns {{ ok: boolean, errors: Record<string,string> }}
 */
export function validateCardDraft(draft, now = new Date()) {
  const d = draft || {};
  const errors = {};
  const title = String(d.title || "").trim();
  const body = String(d.body || "").trim();
  const cta = String(d.ctaLabel || "").trim();
  if (!title) errors.title = "제목을 적어 주세요";
  else if (title.length > CARD_TITLE_MAX) errors.title = `제목은 ${CARD_TITLE_MAX}자까지예요`;
  if (!body) errors.body = "내용을 적어 주세요";
  else if (body.length > CARD_BODY_MAX) errors.body = `내용은 ${CARD_BODY_MAX}자까지예요`;
  if (normalizeCardLink(d.link).kind === "invalid") errors.link = "링크는 /로 시작하는 레이블리 주소나 https:// 주소만 쓸 수 있어요";
  if (cta.length > CARD_CTA_MAX) errors.ctaLabel = `버튼 글자는 ${CARD_CTA_MAX}자까지예요`;
  if (!CARD_TONES.includes(d.tone || "brand")) errors.tone = "카드 색을 골라 주세요";
  const ends = endOfKstDayIso(d.endDate);
  if (!ends) errors.endDate = "보이기 끝 날짜를 골라 주세요";
  else {
    const t = new Date(ends).getTime();
    if (t <= now.getTime()) errors.endDate = "보이기 끝 날짜는 오늘 이후여야 해요";
    else if (t > now.getTime() + CARD_MAX_DAYS * 86400000) errors.endDate = `카드는 최대 ${CARD_MAX_DAYS}일까지 보일 수 있어요`;
  }
  if (!d.audience) errors.audience = "받는 사람을 골라 주세요";
  return { ok: Object.keys(errors).length === 0, errors };
}

/** 홈에 펼칠 카드와 접어 둘 카드 — 최근 것이 위(서버 순서 그대로) */
export function splitVisibleCards(cards, expanded = false, limit = CARD_VISIBLE_DEFAULT) {
  const list = Array.isArray(cards) ? cards : [];
  if (expanded || list.length <= limit) return { shown: list, hidden: 0 };
  return { shown: list.slice(0, limit), hidden: list.length - limit };
}

/** 발송 내역 상태 → 표시 */
export const CARD_STATUS_LABEL = { active: "보이는 중", ended: "끝남", cancelled: "내림" };
